import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DealTracker from '../components/DealTracker';
import BuyerScreening from '../components/BuyerScreening';
import ContractViewer from '../components/ContractViewer';
import './Portal.css';

function BuyerPortal({ user }) {
  const [offers, setOffers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('offers');
  const [showScreeningModal, setShowScreeningModal] = useState(false);
  const [showContractsModal, setShowContractsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    fetchMyOffers();
    fetchMyTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMyOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/offers/my-offers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(response.data.offers || []);
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  const fetchMyTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/dealtracker/user/' + user.id + '/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const acceptCounterOffer = async (counterOfferId) => {
    if (!window.confirm('Accept this counter offer?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/offers/counter/${counterOfferId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Counter offer accepted! Transaction started.');
      fetchMyOffers();
      fetchMyTransactions();
      setActiveTab('transactions');
    } catch (error) {
      alert('Error accepting counter offer');
    }
  };

  const declineCounterOffer = async (offerId) => {
    if (!window.confirm('Decline this counter offer? Your original offer will be withdrawn.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/offers/${offerId}/withdraw`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Counter offer declined. Your offer has been withdrawn.');
      fetchMyOffers();
    } catch (error) {
      alert('Error declining counter offer');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'badge-warning', text: 'Pending' },
      accepted: { class: 'badge-success', text: 'Accepted' },
      rejected: { class: 'badge-danger', text: 'Rejected' },
      countered: { class: 'badge-info', text: 'Countered' },
      withdrawn: { class: 'badge-secondary', text: 'Withdrawn' }
    };
    return badges[status] || { class: 'badge-secondary', text: status };
  };

  return (
    <div className="portal">
      <div className="portal-header">
        <h1>Buyer Portal</h1>
      </div>

      {/* Alert for counter offers */}
      {offers.filter(o => o.status === 'countered').length > 0 && (
        <div className="alert alert-info">
          <strong>You have counter offers waiting!</strong> Review them in the Offers tab.
        </div>
      )}

      {/* Alert for accepted offers needing screening */}
      {transactions.length > 0 && (
        <div className="alert alert-warning">
          <strong>Action Required:</strong> Complete your buyer screening documents for active transactions.
          <button className="btn btn-sm btn-primary ml-2" onClick={() => setActiveTab('screening')}>
            Complete Screening
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="portal-tabs">
        <button 
          className={`tab ${activeTab === 'offers' ? 'active' : ''}`}
          onClick={() => setActiveTab('offers')}
        >
          My Offers ({offers.length})
        </button>
        <button 
          className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          Transactions ({transactions.length})
        </button>
        <button 
          className={`tab ${activeTab === 'screening' ? 'active' : ''}`}
          onClick={() => setActiveTab('screening')}
        >
          Documents
        </button>
      </div>

      <div className="portal-content">
        {/* Offers Tab */}
        {activeTab === 'offers' && (
          <>
            <div className="button-header">
              <a href="/listings" className="btn btn-primary">Browse Listings</a>
            </div>

            {offers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-content">
                  <div className="empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                    </svg>
                  </div>
                  <h3>Ready to find your next home?</h3>
                  <p>Your offers will appear here once you start exploring.</p>
                </div>
              </div>
            ) : (
              <div className="offers-list">
                {offers.map(offer => {
                  const badge = getStatusBadge(offer.status);
                  return (
                    <div key={offer.id} className={`offer-card ${offer.status === 'countered' ? 'highlight' : ''}`}>
                      <div className="offer-card-header">
                        <div>
                          <h3>{offer.listing_title}</h3>
                          <p className="listing-address">
                            {offer.listing_address}, {offer.listing_city}, {offer.listing_state}
                          </p>
                        </div>
                        <span className={`badge ${badge.class}`}>
                          {badge.text}
                        </span>
                      </div>

                      <div className="offer-details">
                        <div className="offer-detail">
                          <span className="label">Listing Price:</span>
                          <span className="value">${offer.listing_price?.toLocaleString()}</span>
                        </div>
                        <div className="offer-detail">
                          <span className="label">Your Offer:</span>
                          <span className="value offer-amount">${offer.offer_amount?.toLocaleString()}</span>
                        </div>
                        {offer.closing_date && (
                          <div className="offer-detail">
                            <span className="label">Closing Date:</span>
                            <span className="value">{new Date(offer.closing_date).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>

                      {/* Counter Offer Section */}
                      {offer.status === 'countered' && offer.counter_offers && offer.counter_offers.length > 0 && (
                        <div className="counter-offer-section">
                          <h4>Counter Offer Received!</h4>
                          <p>The seller has countered with: <strong>${offer.counter_offers[0].counter_amount?.toLocaleString()}</strong></p>
                          {offer.counter_offers[0].message && (
                            <p className="counter-message">"{offer.counter_offers[0].message}"</p>
                          )}
                          <div className="counter-actions">
                            <button 
                              className="btn btn-success"
                              onClick={() => acceptCounterOffer(offer.counter_offers[0].id)}
                            >
                              Accept Counter
                            </button>
                            <button 
                              className="btn btn-secondary"
                              onClick={() => declineCounterOffer(offer.id)}
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                      )}

                      {offer.status === 'accepted' && (
                        <div className="accepted-section">
                          <h4>Congratulations!</h4>
                          <p>Your offer was accepted. View the transaction in the Active Transactions tab.</p>
                        </div>
                      )}

                      <div className="offer-meta">
                        <span>Submitted: {new Date(offer.created_at).toLocaleDateString()}</span>
                        <span>Seller: {offer.seller_first_name} {offer.seller_last_name}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <>
            {transactions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-content">
                  <div className="empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                    </svg>
                  </div>
                  <h3>No transactions yet</h3>
                  <p>Once a seller accepts your offer, your transaction will appear here.</p>
                </div>
              </div>
            ) : (
              <div className="transactions-list">
                {transactions.map(transaction => (
                  <div key={transaction.id} className="transaction-card">
                    <div className="transaction-header">
                      <h3>{transaction.listing_title || 'Property Purchase'}</h3>
                      <span className="transaction-price">
                        ${transaction.final_price?.toLocaleString()}
                      </span>
                    </div>

                    <div className="transaction-info">
                      <p><strong>Seller:</strong> {transaction.seller_name}</p>
                      <p><strong>Address:</strong> {transaction.listing_address}</p>
                    </div>
                    
                    <DealTracker transactionId={transaction.id} userType="buyer" />
                    
                    <div className="next-steps">
                      <h4>Your Next Steps:</h4>
                      <ul>
                        <li>Complete buyer screening documents</li>
                        <li>Review and sign contracts</li>
                        <li>Schedule inspections</li>
                        <li>Secure financing</li>
                      </ul>
                    </div>

                    <div className="transaction-actions">
                      <button 
                        className="btn btn-primary"
                        onClick={() => { setSelectedTransaction(transaction); setShowScreeningModal(true); }}
                      >
                        Upload Documents
                      </button>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => { setSelectedTransaction(transaction); setShowContractsModal(true); }}
                      >
                        View Contracts
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Screening Tab */}
        {activeTab === 'screening' && (
          <BuyerScreening 
            buyerId={user.id} 
            transactionId={transactions[0]?.id}
            userType="buyer"
          />
        )}
      </div>

      {/* Screening Modal */}
      {showScreeningModal && selectedTransaction && (
        <div className="modal-overlay" onClick={() => setShowScreeningModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload Documents</h3>
              <button className="modal-close" onClick={() => setShowScreeningModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <BuyerScreening 
                buyerId={user.id} 
                transactionId={selectedTransaction.id}
                userType="buyer"
              />
            </div>
          </div>
        </div>
      )}

      {/* Contracts Modal */}
      {showContractsModal && selectedTransaction && (
        <div className="modal-overlay" onClick={() => setShowContractsModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transaction Contracts</h3>
              <button className="modal-close" onClick={() => setShowContractsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <ContractViewer 
                transactionId={selectedTransaction.id}
                listingId={selectedTransaction.listing_id}
                userType="buyer" 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BuyerPortal;
